import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyRefreshQuery } from '../../slices/userApiSlice';
import { setCredentials, logout } from '../../slices/authSlice';
import { fetchActiveGlobalDiscount } from '../../slices/activeGlobalDiscountSlice';
import { useGetCartQuery, cartApiSlice } from '../../slices/cartApiSlice';
import { orderApiSlice } from '../../slices/orderApiSlice';
import { setCartState } from '../../slices/cartSlice';
import { clearActiveOrder } from '../../slices/orderSlice';
import { clearGuestId } from '../../utils/sessionUtils';

const AuthHandler = () => {
    const dispatch = useDispatch();
    const userId = useSelector((state) => state.auth.userInfo?._id || null);
    const fetchedUserIdRef = useRef(null);
    const [triggerRefresh, { data, isError, isSuccess }] = useLazyRefreshQuery();

    // We fetch the active cart always. 
    // If we are logged in, it fetches the user's cart.
    // If we are guest, it fetches the guest cart.
    const { data: activeCart, isSuccess: activeCartSuccess } = useGetCartQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        triggerRefresh();
    }, [triggerRefresh]);

    useEffect(() => {
        if (!userId) {
            fetchedUserIdRef.current = null;
            return;
        }

        if (fetchedUserIdRef.current === userId) {
            return;
        }

        fetchedUserIdRef.current = userId;
        dispatch(fetchActiveGlobalDiscount());
    }, [dispatch, userId]);

    useEffect(() => {
        if (isSuccess && data) {
            dispatch(setCredentials(data));
            clearGuestId();
            
            // Trigger immediate update
            // Backend now awaits merge, so we don't need artificial delays
            dispatch(cartApiSlice.util.invalidateTags(['Cart']));
            dispatch(orderApiSlice.util.invalidateTags(['Order']));
            
            // Explicitly initiate fetch to ensure data is updated immediately
            dispatch(cartApiSlice.endpoints.getCart.initiate(undefined, { forceRefetch: true }));
        }

        if (isError) {
            dispatch(logout());
            dispatch(clearActiveOrder());
        }
    }, [isSuccess, isError, data, dispatch]);

    useEffect(() => {
        if (activeCartSuccess && activeCart) {
            dispatch(setCartState(activeCart));
        }
    }, [activeCartSuccess, activeCart, dispatch]);

    return null; // No UI
};

export default AuthHandler;
